import React from 'react';
import { Card } from '@/components/ui/card';
import { Star, MapPin, Home, DollarSign, Clock } from 'lucide-react';

const testimonials = [
  {
    id: 1,
    name: "Sarah M.",
    location: "Frisco, TX",
    role: "Seller",
    rating: 5,
    date: "March 2025",
    image: "https://ui-avatars.com/api/?name=Sarah+M&background=f97316&color=fff",
    text: "Listed my home on Its-Sold.com and sold it in just 3 weeks! No agent fees meant I kept an extra $18,000. The platform was so easy to use, and I got quality inquiries from serious buyers. Highly recommend!",
    property: "4BR Home in Frisco",
    saved: "$18,000"
  },
  {
    id: 2,
    name: "Michael R.",
    location: "Plano, TX",
    role: "Buyer",
    rating: 5,
    date: "June 2025",
    image: "https://ui-avatars.com/api/?name=Michael+R&background=2563eb&color=fff",
    text: "Found my dream home without dealing with pushy agents. Contacted the seller directly, negotiated a fair price, and closed in 30 days. The FSBO approach saved us both money!",
    property: "3BR Townhouse",
    saved: "$12,000"
  },
  {
    id: 3,
    name: "Jennifer L.",
    location: "McKinney, TX",
    role: "Seller",
    rating: 5,
    date: "April 2025",
    image: "https://ui-avatars.com/api/?name=Jennifer+L&background=f97316&color=fff",
    text: "As a first-time FSBO seller, I was nervous. But Its-Sold.com made it simple! The AI helped me write a great listing description, and the price comparison tool showed me I was priced right. Sold in 5 weeks and saved over $20K in commissions!",
    property: "5BR Family Home",
    saved: "$20,500"
  },
  {
    id: 4,
    name: "David K.",
    location: "Dallas, TX",
    role: "Buyer",
    rating: 5,
    date: "August 2025",
    image: "https://ui-avatars.com/api/?name=David+K&background=2563eb&color=fff",
    text: "Best home buying experience ever! No agent pressure, direct communication with sellers, and better prices because there's no agent commission baked in. Found my condo in Uptown Dallas for $15K less than comparable listings with agents.",
    property: "2BR Condo in Uptown",
    saved: "$15,000"
  },
  {
    id: 5,
    name: "Amanda T.",
    location: "Arlington, TX",
    role: "Seller",
    rating: 5,
    date: "September 2025",
    image: "https://ui-avatars.com/api/?name=Amanda+T&background=f97316&color=fff",
    text: "I upgraded to Premium and it was worth every penny! My listing was featured on the homepage and I had 12 showing requests in the first week. Sold for asking price in 2 weeks. The platform pays for itself!",
    property: "4BR Ranch Home",
    saved: "$16,800"
  },
  {
    id: 6,
    name: "Robert H.",
    location: "Fort Worth, TX",
    role: "Buyer",
    rating: 5,
    date: "July 2025",
    image: "https://ui-avatars.com/api/?name=Robert+H&background=2563eb&color=fff",
    text: "After months of dealing with agents, I found Its-Sold.com and it changed everything. Direct seller contact meant honest conversations about the property. No games, no pressure. Closed on my Fort Worth home last month!",
    property: "3BR Traditional Home",
    saved: "$13,500"
  },
  {
    id: 7,
    name: "Lisa P.",
    location: "Allen, TX",
    role: "Seller",
    rating: 5,
    date: "May 2025",
    image: "https://ui-avatars.com/api/?name=Lisa+P&background=f97316&color=fff",
    text: "Sold my home without an agent and couldn't be happier! The platform's resources and guides were incredibly helpful. I felt supported throughout the entire process. Saved $22,000 in commissions!",
    property: "5BR Executive Home",
    saved: "$22,000"
  },
  {
    id: 8,
    name: "James W.",
    location: "Richardson, TX",
    role: "Buyer",
    rating: 5,
    date: "October 2025",
    image: "https://ui-avatars.com/api/?name=James+W&background=2563eb&color=fff",
    text: "Its-Sold.com is a game-changer for DFW home buyers. Found multiple homes in Richardson, all priced better than agent listings. Sellers are motivated and easy to work with. This is the future of real estate!",
    property: "4BR Modern Home",
    saved: "$17,200"
  }
];

const TestimonialsSection = ({ showAll = false, maxCards = 3 }) => {
  const displayedTestimonials = showAll ? testimonials : testimonials.slice(0, maxCards);

  return (
    <div className="py-12 bg-gradient-to-br from-slate-50 to-orange-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display' }}>
            ⭐ Real Success Stories from DFW Homeowners
          </h2>
          <p className="text-lg text-gray-600">
            Join thousands of satisfied buyers and sellers who chose the FSBO path
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {displayedTestimonials.map((testimonial) => (
            <Card key={testimonial.id} className="p-6 hover:shadow-xl transition-all">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-xs text-gray-500 flex items-center">
                      <MapPin className="w-3 h-3 mr-1" />
                      {testimonial.location}
                    </p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                  testimonial.role === 'Seller' 
                    ? 'bg-orange-100 text-orange-700' 
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  {testimonial.role}
                </span>
              </div>

              {/* Rating */}
              <div className="flex items-center mb-3">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                ))}
                <span className="text-xs text-gray-500 ml-2">{testimonial.date}</span>
              </div>

              {/* Review Text */}
              <p className="text-sm text-gray-700 mb-4 italic">
                "{testimonial.text}"
              </p>

              {/* Property Details */}
              <div className="border-t pt-3 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 flex items-center">
                    <Home className="w-3 h-3 mr-1" />
                    {testimonial.property}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600 flex items-center">
                    <DollarSign className="w-3 h-3 mr-1" />
                    Saved
                  </span>
                  <span className="text-lg font-extrabold text-orange-600">
                    {testimonial.saved}
                  </span>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Trust Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
          <div className="text-center p-4 bg-white rounded-lg shadow-sm">
            <div className="text-3xl font-bold text-orange-600">DFW</div>
            <div className="text-sm text-gray-600">Wide Coverage</div>
          </div>
          <div className="text-center p-4 bg-white rounded-lg shadow-sm">
            <div className="text-3xl font-bold text-orange-600">$18K</div>
            <div className="text-sm text-gray-600">Avg. Saved</div>
          </div>
          <div className="text-center p-4 bg-white rounded-lg shadow-sm">
            <div className="text-3xl font-bold text-orange-600">4.9⭐</div>
            <div className="text-sm text-gray-600">Average Rating</div>
          </div>
          <div className="text-center p-4 bg-white rounded-lg shadow-sm">
            <div className="text-3xl font-bold text-orange-600">24/7</div>
            <div className="text-sm text-gray-600">Platform Access</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestimonialsSection;
